import React, { useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';

import { useGetLivestreamingMonthlyTrendQuery } from '../../redux/apis/analyticsManagement/aMLiveStreamingStatsApiSlice';
import ComponentLoader from '../other/ComponentLoader';
import { Card, CardBody, CardExpandToggler } from '../other/card/card';
import getLivestreamingMonthlyTrendOptions from './charts/livestreamingMonthlyTrendOptions';

const MONTHLY_TREND_CHART_HEIGHT = 350;

const formatMonthAxis = monthStr => {
  const m = /^(\d{4})-(\d{2})/.exec(String(monthStr ?? ''));
  if (!m) return String(monthStr ?? '');
  const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  const mon = d.toLocaleDateString('en-GB', { month: 'short' });
  const yy = m[1].slice(-2);
  return `${mon} ${yy}`;
};

const LiveStreamingMonthlyTrend = () => {
  const { data, isLoading, isError, error } = useGetLivestreamingMonthlyTrendQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [insightView, setInsightView] = useState('reported');
  const [chartThemeKey, setChartThemeKey] = useState(0);

  useEffect(() => {
    const onThemeReload = () => setChartThemeKey(k => k + 1);
    document.addEventListener('theme-reload', onThemeReload);
    return () => document.removeEventListener('theme-reload', onThemeReload);
  }, []);

  const sorted = useMemo(() => {
    const rows = Array.isArray(data) ? [...data] : [];
    rows.sort((a, b) => String(a?.month ?? '').localeCompare(String(b?.month ?? '')));
    return rows;
  }, [data]);

  const categories = useMemo(() => sorted.map(r => formatMonthAxis(r?.month)), [sorted]);

  const series = useMemo(() => {
    if (insightView === 'removed') {
      return [{ name: 'Removed', data: sorted.map(r => Number(r?.removed) || 0) }];
    }
    return [{ name: 'Reported', data: sorted.map(r => Number(r?.reported) || 0) }];
  }, [sorted, insightView]);

  const chartOptions = useMemo(
    () => getLivestreamingMonthlyTrendOptions(categories, insightView),
    [categories, insightView]
  );
  const hasData = sorted.length > 0;

  const toggleClass = key =>
    `btn btn-sm bg-transparent ${insightView === key ? 'text-theme border-theme' : 'text-body-secondary border-secondary opacity-75'}`;

  return (
    <div className='row g-2 mt-1'>
      <div className='col-12'>
        <Card className='mb-0'>
          <CardBody className='relative p-0'>
            {isError ? (
              <div className='absolute inset-0 d-flex items-center justify-center p-3 text-danger small bg-white bg-opacity-75'>
                Failed to load.
                {error?.data?.message ? ` ${error.data.message}` : ''}
              </div>
            ) : null}

            <div className='p-4 d-flex flex-column gap-2'>
              <div className='d-flex align-items-start justify-content-between gap-2 flex-wrap'>
                <div className='d-flex flex-column gap-2'>
                  <div className='d-flex fw-bold small'>Platform Insights</div>
                  <div className='d-flex flex-wrap align-items-center gap-2'>
                    <button type='button' className={toggleClass('reported')} onClick={() => setInsightView('reported')}>
                      Reported
                    </button>
                    <button type='button' className={toggleClass('removed')} onClick={() => setInsightView('removed')}>
                      Removed
                    </button>
                  </div>
                </div>
                <div className='align-self-start'>
                  <CardExpandToggler />
                </div>
              </div>
            </div>

            <div className='p-4 pt-0'>
              <div className='small text-body-secondary'>
                <div className='mt-2 position-relative' style={{ height: MONTHLY_TREND_CHART_HEIGHT }}>
                  {isLoading ? <ComponentLoader /> : null}
                  {hasData ? (
                    <Chart
                      key={`ls-platform-insights-${chartThemeKey}-${insightView}`}
                      type='area'
                      height={MONTHLY_TREND_CHART_HEIGHT}
                      options={chartOptions}
                      series={series}
                    />
                  ) : !isLoading ? (
                    <div className='d-flex text-base h-100 align-items-center justify-content-center text-center px-2'>
                      No trend data for the current year.
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default LiveStreamingMonthlyTrend;
